<template>
  <div class="phases">
    <header class="container-header max-w-6xl mx-auto mb-6 lg:mb-14">
      <h1 class="title mb-9">
        Enter Nutrient Details
      </h1>
      <p class="text-grey-dark text-sm lg:text-lg text-center">
        Enter the nutrient specifications to generate the result matrix.
      </p>
    </header>

    <!-- <div class="container-bg flex flex-col lg:flex-row justify-between p-4 md:px-8 md:py-11 xl:p-6 2xl:px-14 mb-8 md:mb-11">
      <div class="flex justify-between items-center space-x-4 lg:space-x-8">
        <div class="rounded-xl bg-white text-center flex items-center justify-center py-2 px-4 lg:px-8">
          <p v-if="getSelectedProduct === 'proact360'" class="text-grey-dark text-sm font-medium">
            <span class="font-bold">ProAct 360<sup>TM</sup></span> Dose
          </p>
          <p v-else class="text-grey-dark text-sm font-medium mb-2.5">
            <span class="font-bold">ProAct</span> Dose
          </p>

          <form-field-select
            class="py-3 px-3.5 lg:px-7 xl:px-6 2xl:px-7 lg:py-5"
            v-model="activeTab.dose"
            trackBy="value"
            optionLabel="name"
            :options="doses"
          />
        </div>
      </div>
    </div> -->

    <div class="container-bg p-4 md:px-8 md:py-11 xl:p-6 2xl:p-14 z-0">
      <div class="tabs-container">
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:gap-6 2xl:gap-10 xl:grid-cols-2">
          <!-- Nutrients Component -->
          <div class="wrapper">
            <nutrients
              v-if="activeTab.nutritional_values"
              v-model="activeTab.nutritional_values"
            />
          </div>

          <!-- Result Component -->
             <div class="flex flex-col items-end">
            <div class="rounded-xl bg-white text-center flex-grow-0 flex items-center justify-center py-2 px-4 lg:px-8 mb-4">
              <p v-if="getSelectedProduct === 'proact360'" class="text-grey-dark text-sm font-medium">
              <span class="font-bold">ProAct 360<sup>TM</sup></span> Dose (g/MT)
              </p>
              <p v-else class="text-grey-dark text-sm font-medium mb-2.5">
              <span class="font-bold">ProAct</span> Dose (g/MT)
              </p>

              <form-field-select
              class="py-2 px-3.5 lg:px-7 xl:px-6 2xl:px-7 lg:py-4"
              v-model="activeTab.dose"
              trackBy="value"
              optionLabel="name"
              :options="doses"
              />
            </div>
            <div class="wrapper lg:col-span-2 xl:col-span-1 overflow-x-auto w-full">
              <div class="inline-block min-w-full">
              <result
                v-if="activeTab.name"
                v-model="phaseResult"
                :name="activeTab.name"
                @change-nutrient="onChangeResult"
              />
              </div>
            </div>
             </div>
        </div>
      </div>
    </div>

    <modal
      ref="modal"
      :class="{'modal-how-it-work': isOpenModalHowItWork}"
      :isOpen="isModalOpen"
      @close="onModalCloseAll"
    >
      <template #title>
        <p></p>
        <template v-if="isOpenModalHowItWork">
          <div class="flex flex-col justify-start lg:divide-y-2">
            <img src="@/assets/images/how-it-work/slide-1.png" alt="How it works" class="mx-auto mb-6 w-full h-auto">
            <img src="@/assets/images/how-it-work/slide-2.png" alt="How it works" class="mx-auto mt-6 w-full h-auto">
            <img src="@/assets/images/how-it-work/slide-3.png" alt="How it works" class="mx-auto mt-6 w-full h-auto">
          </div>
        </template>
      </template>

      <template #button>
        <button
          class="btn-success"
          @click="onModalCloseAll"
        >
          Got it
        </button>
      </template>
    </modal>

    <Navigation
      v-bind="{prev: {disabled: false}, next: {disabled: false}}"
      @click="onValidateNextTick"
    >

    </Navigation>
  </div>
</template>

<script>
import Navigation from '@/components/Navigation';
import Modal from '@/components/Modal';
import Nutrients from '@/components/phases/Nutrients';
import Result from '@/components/phases/Result';
import ModalMixin from '@/mixins/ModalMixin';
import NavigationMixin from '@/mixins/NavigationMixin';
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import debounce from 'lodash.debounce';
import { v4 as uuidv4 } from 'uuid';
import FormFieldSelect from '../components/form/FormFieldSelect.vue';

export default {
  name: 'PhaseAqua',

  mixins: [
    ModalMixin,
    NavigationMixin
  ],

  components: {
    Nutrients,
    Result,
    Modal,
    Navigation,
    FormFieldSelect
  },

  data: () => ({
    activeTab: {
      nutritional_values: [],
      name: 'Aqua Phase',
      dose: 200,
    },
    isOpenModalHowItWork: false,
    doses: [
      { name: '200', value: 200 },
      { name: '400', value: 400 },
      { name: '600', value: 600 },
    ]
  }),

  created() {
    this.onChangeResult = debounce((recommendedEnergy) => {
      this.activeTab.recommendedEnergy = recommendedEnergy;
    }, 1000);
  },

  mounted() {
    this.initializePhase();
  },

  methods: {
    ...mapActions({
      onCalculatePhaseMatrix: 'phase/onCalculatePhaseMatrix',
      onSaveDiet: 'feed/onSaveDiet'
    }),

    ...mapMutations({
      setResultMatrix: 'result/setResultMatrix',
      updateActiveDiet: 'feed/setActiveDiet', // Vuex action to update activeDiet
    }),

    initializePhase() {
      const activeDiet = this.$store.state.feed.activeDiet;

      // Check if activeDiet exists and has a valid singleton phase
      if (
        activeDiet &&
        activeDiet.diet_phases &&
        activeDiet.diet_phases.length === 1 &&
        activeDiet.productGroup === 'Aqua'
      ) {
        // Load the existing singleton phase into activeTab
        const singletonPhase = activeDiet.diet_phases[0];
        this.activeTab = {
          nutritional_values: singletonPhase.nutritional_values || [],
          name: singletonPhase.name || 'Aqua Phase',
          dose: singletonPhase.dose || 200,
        };
      } else {
        // Nutrients are loaded, process them
        if (this.$store.state.phase.nutrients && this.$store.state.phase.nutrients.length > 0) {
          this.activeTab.nutritional_values = this.$store.state.phase.nutrients
              .filter(nutrient => nutrient.is_aqua || nutrient.nutrientName === 'CP') // Filter out nutrients where is_aqua is false
              .map(nutrient => {
                if (nutrient.nutrientName === 'CP' || nutrient.nutrientName === 'd. Lys') {
                  return {
                    ...nutrient,
                    displayName: this.activeAnimalType.type === 'swine' && nutrient.display_name_swine
                      ? nutrient.display_name_swine
                      : nutrient.nutrientName,
                  };
                } else {
                  return {
                    ...nutrient,
                    displayName: this.activeAnimalType.type === 'swine'
                      ? nutrient.display_name_swine
                      : nutrient.nutrientName,
                    ratio: nutrient[this.getActiveRatioByAnimalType],
                  };
                }
              });
        } else {
          // Nutrients are not loaded yet, retry after a delay
          setTimeout(this.initializePhase, 100); // Retry after 100ms
        }

        // Assign unique IDs to each nutrient
        this.activeTab.nutritional_values.forEach(el => {
          el.id = `temp-${uuidv4()}`;
        });
      }
    },

    onChangeResult() {},

    onValidateNextTick($event) {
      if ($event === 'next' && !this.activeTab.nutritional_values.find(el => el.nutrientName === 'CP').value) {
        this.onModalOpenCPValidation();
        return;
      }
      this.onNavigationClick($event, 'feed-view', 'result');
    },

    handleSaveDiet() {
      this.onSaveDiet({ diet: this.activeDiet }).then(res => {
        this.$toast.open({
          message: `Diet <strong>${res.diet.name}</strong> was saved!`
        });
      });
    },

    onModalOpenHowItWork() {
      this.isOpenModalHowItWork = true;
      this.onModalOpen();
    },

    onModalCloseAll() {
      this.isOpenModalHowItWork = false;
      this.onModalClose();
    }
  },

  computed: {
    ...mapState({
      phaseResult: state => state.phase.phaseResult
    }),

    ...mapGetters({
      activeAnimalType: 'animalType/getActiveAnimalType',
      getActiveRatioByAnimalType: 'animalType/getActiveRatioByAnimalType',
      getSelectedProduct: 'productSelect/getSelectedProduct',
      activeDiet: 'feed/getActiveDiet',
    })
  },

  watch: {
    'activeTab': {
      handler: debounce(function(current, pre) {
        /* eslint-disable no-invalid-this */
        // this.onCalculateAquaPhaseMatrix({
        //   dose: this.activeTab.dose,
        //   nutritionalValues: current.nutritional_values,
        //   animal: this.activeAnimalType?.subtype
        // })
        const diet = {
          id: 'temp-aqua', // Aqua diets don't have a predefined ID
          name: 'Aqua Diet', // Default name for Aqua diets
          ingredientDigestibility: null,
          animal_id: this.activeAnimalType?.id || null,
          created_at: new Date().toISOString(), // Use the current timestamp
          productGroup: 'Aqua',
          diet_phases: [
            {
              id: null, // No predefined ID for the phase
              name: current.name,
              order: 1, // Singleton phase, so order is always 1
              dose: current.dose,
              safetyMargin: 0, // Aqua diets may not use safety margins
              feedIntake: 1, // Aqua diets may not use feed intake
              recommendedEnergy: 25, // Aqua diets may not use recommended energy
              ingredients: [], // Aqua diets have no ingredients
              nutritional_values: current.nutritional_values,
              phaseResult: [], // Initialize phaseResult as an empty array
            },
          ],
          allIngredients: [], // Aqua diets have no ingredients
        }
        this.updateActiveDiet(diet);

        this.onCalculatePhaseMatrix({
          phase: diet.diet_phases[0],
          ingredientDigestibility: null,
          animal: this.activeAnimalType?.subtype
        })

        if (Object.keys(pre).length) {
          this.setResultMatrix({})
        }
      }, 500),
      deep: true
    },
  }
};
</script>

<style scoped>
>>> .how-it-work svg {
  width: 100%;
  height: auto;
}
>>> .modal-how-it-work .sweet-modal {
  max-width: 70%;
}
</style>
